import { ActivityEntry, Project } from '@core/models';

/** Top-level keys never worth logging as an edit (or logged elsewhere). */
const IGNORED = new Set<string>(['activity']);

const MAX_ENTRIES = 200;
const MERGE_WINDOW_MS = 5 * 60 * 1000;

/**
 * Map the set of changed top-level project keys to a History group label, or
 * null when the change is captured elsewhere (phase advances → phaseLog;
 * punch-lists & financials have their own surfaces). Mirrors the prototype's
 * `activityGroup`.
 */
function activityGroup(keys: string[]): string | null {
  if (!keys.length) return null;
  const has = (k: string) => keys.includes(k);
  const some = (re: RegExp) => keys.some((k) => re.test(k));

  // Punch-lists, financials, and phase-checklist sign-offs have their own
  // surfaces (checklist sign-offs log an explicit per-item entry instead).
  if (keys.every((k) => ['issues', 'nextSteps', 'budget', 'changeOrders', 'billings', 'phaseChecklists'].includes(k))) return null;
  // Phase moves are captured via phaseLog.
  if (has('currentPhaseNum') || (has('phaseLog') && keys.length <= 2)) return null;
  if (has('ops')) return 'Field & Risk data';
  if (has('workSessions')) return 'Work schedule';
  if (some(/contract|budget|^start$|^end$|actualStart|tradeProfile|laborPct|laborRate|^crew$|phaseDates|manpowerReq|subAlloc|activities|mgmt/)) return 'Contract & schedule';
  if (has('state')) return 'Project state';
  if (has('status')) return 'Health status';
  if (has('team')) return 'Team';
  if (has('contacts')) return 'Contacts';
  if (some(/address|location/)) return 'Site address';
  if (has('description')) return 'Description';
  if (has('type') || has('osId') || has('contractType') || has('trades') || has('division')) return 'Project setup';
  return 'Project data';
}

function deepEq(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  return JSON.stringify(a) === JSON.stringify(b);
}

/** The set of top-level keys that differ between two project snapshots. */
function changedKeys(prev: Project, next: Project): string[] {
  const keys = new Set<string>();
  const p = prev as unknown as Record<string, unknown>;
  const n = next as unknown as Record<string, unknown>;
  const all = new Set([...Object.keys(p), ...Object.keys(n)]);
  for (const k of all) {
    if (IGNORED.has(k)) continue;
    if (!deepEq(p[k], n[k])) keys.add(k);
  }
  return [...keys];
}

/**
 * Return `next` with a grouped activity entry appended for whatever changed vs
 * `prev`. Same-area edits by the same person within a few minutes collapse into
 * a single counted entry. Returns `next` unchanged when nothing loggable moved.
 */
export function withActivityLog(
  prev: Project,
  next: Project,
  actor: { id: string; name: string } | null,
): Project {
  const grp = activityGroup(changedKeys(prev, next));
  if (!grp) return next;

  const nowIso = new Date().toISOString();
  const by = actor?.id ?? '';
  const byName = actor?.name ?? 'You';
  const activity: ActivityEntry[] = [...(prev.activity ?? [])];
  const last = activity[activity.length - 1];

  if (last && !last.text && last.by === by && last.group === grp && Date.now() - new Date(last.at).getTime() < MERGE_WINDOW_MS) {
    activity[activity.length - 1] = { ...last, at: nowIso, count: (last.count || 1) + 1 };
  } else {
    activity.push({ by, byName, at: nowIso, group: grp, count: 1 });
    if (activity.length > MAX_ENTRIES) activity.splice(0, activity.length - MAX_ENTRIES);
  }
  return { ...next, activity };
}
